import React, { useEffect, useMemo, useState } from 'react';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import type { KeyedAttachmentStaging } from '@/hooks/useKeyedAttachmentStaging';
import { CatalogLineItems, useCatalogLines } from '@/features/cost-management/purchase-orders/components/CatalogLineItems';
import { usePurchaseOrderFormOptions } from '@/features/cost-management/purchase-orders/usePurchaseOrderFormOptions';
import { linesProblem } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { formatMoney, lineCostCodeProblem, linesTotal, type LineItem } from '@/lib/line-items';
import { isTempId } from './store';
import type { EditableOption } from './SelectionOptionsEditor';

/** The items of one option as the Selection form holds them until it is saved: `initial` is how they were loaded, to know whether anything changed. */
export interface OptionItemsDraft {
  lines: LineItem[];
  initial: string;
}

export const serializeItems = (lines: LineItem[]) => JSON.stringify(lines.map(({ key: _key, ...rest }) => rest));

const numeric = (value: unknown) => String(value ?? '').replace(/[^0-9.]/g, '');

interface SelectionOptionModalProps {
  isOpen: boolean;
  option: EditableOption | null;
  /** The option's items (null while they are being read). */
  items: OptionItemsDraft | null;
  /** Builder Price -- and the items, whose prices ARE the builder's cost -- only for callers the server lets receive it. */
  canViewBuilderPrice: boolean;
  /** The selection's Project: where each file's Browse Drive looks. */
  projectId: string;
  staging: KeyedAttachmentStaging;
  /** "Done": the option and its items go back to the Selection form (they are saved with the Selection). */
  onApply: (option: EditableOption, items: OptionItemsDraft) => void;
  /** Back to the Selection without applying anything. */
  onClose: () => void;
}

/**
 * ONE option of a Selection, in its own modal over the Selection's: its data (title, link, prices, description), its files and ITS items -- the generic item
 * table every document uses (CatalogLineItems), independent of the other options' items. Nothing is sent from here: "Done" hands the option back to the
 * Selection form, whose Save Changes persists the option and its items through the API. The Builder Price is always the total of the items ($0 with none) and cannot be typed.
 */
export function SelectionOptionModal({ isOpen, option, items, canViewBuilderPrice, projectId, staging, onApply, onClose }: SelectionOptionModalProps) {
  const { vendors } = usePurchaseOrderFormOptions();
  const [lines, setLines] = useState<LineItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const catalog = useCatalogLines({ isOpen, lines, setLines, vendorId: null, vendors });
  const { setOffersByItem, rememberOffersOf } = catalog;
  const optionId = option?.id ?? null;
  const itemsReady = items !== null;

  // the items the Selection form holds for this option are the starting point each time the modal opens (or they finish loading)
  useEffect(() => {
    setError(null);
    setOffersByItem(new Map());
    if (!isOpen || !items) { setLines([]); return; }
    setLines(items.lines);
    rememberOffersOf(items.lines.map((l) => l.catalogItemId).filter((id): id is string => !!id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, optionId, itemsReady]);

  // (field names are the option's own: the Selection form under this modal has a "name" and a "description" too, and ids must not repeat)
  const initialValues = useMemo(() => ({
    option_name: option?.name ?? '',
    option_link: option?.link ?? '',
    option_client_price: option?.client_price ?? '',
    option_description: option?.description ?? '',
  }), [option]);

  const fields = useMemo<GenericFormField[]>(() => [
    { name: 'option_name', label: 'Option Title', type: 'text', required: true, row: 1, placeholder: 'Enter option title' },
    { name: 'option_link', label: 'Link', type: 'text', row: 1, placeholder: 'https://...' },
    // the Builder Price is the total of the option's items ($0 with none): shown, grey and read-only, never typed
    ...(canViewBuilderPrice ? [{ name: 'option_builder_price', label: 'Builder Price ($)', type: 'text' as const, row: 2, displayValue: formatMoney(linesTotal(lines)) }] : []),
    { name: 'option_client_price', label: 'Client Price ($)', type: 'text', row: 2, placeholder: '$0.00' },
    { name: 'option_description', label: 'Description', type: 'textarea', row: 3, colSpan: 2, placeholder: 'Enter option description' },
  ], [canViewBuilderPrice, lines]);

  if (!option) return null;
  const persisted = !isTempId(option.id);

  const handleFieldChange = (name: string, value: unknown, updated: Record<string, unknown>) => {
    if (name === 'option_client_price' && typeof value === 'string') updated[name] = numeric(value);
  };

  const handleSubmit = (data: Record<string, any>) => {
    setError(null);
    if (!String(data.option_name || '').trim()) { setError('Option Title is required'); return; }
    if (serializeItems(lines) !== (items?.initial ?? '[]')) {
      const problem = linesProblem(lines) || lineCostCodeProblem(lines);
      if (problem) { setError(problem); return; }
    }
    onApply(
      {
        ...option,
        name: String(data.option_name).trim(),
        link: String(data.option_link || ''),
        description: String(data.option_description || ''),
        client_price: numeric(data.option_client_price),
        builder_price: String(linesTotal(lines)),
      },
      { lines, initial: items?.initial ?? '[]' },
    );
  };

  return (
    <GenericForm
      title="Option"
      mode="edit"
      fields={fields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onClose}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save Changes"
      onFieldChange={handleFieldChange}
      additionalSections={() => (
        <>
          {canViewBuilderPrice && (
            <CatalogLineItems
              catalog={catalog}
              lines={lines}
              vendorId={null}
              vendors={vendors}
              loading={!itemsReady}
              disabled={!itemsReady}
            />
          )}
          <GenericAttachmentsSection
            mode="edit"
            projectId={projectId}
            attachments={staging.uploadedFor(option.id)}
            pendingFiles={staging.pendingFor(option.id)}
            onUpload={(files) => staging.upload(option.id, persisted ? option.id : null, files)}
            onRemovePending={(i) => staging.removePending(option.id, i)}
            onDelete={(attachmentId) => staging.remove(option.id, attachmentId)}
          />
        </>
      )}
    />
  );
}
